// src/appPage/AuthSystem.jsx
import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LoginView from "../components/shared/auth/LoginView";
import SignupView from "../components/shared/auth/SignupView";
import ForgotPasswordView from "../components/shared/auth/ForgotPasswordView";
import ResetPasswordView from "../components/shared/auth/ResetPasswordView";

import PendingView from "../components/customer/PendingView";
import ProductsDashboard from "../components/customer/ProductsDashboard";

import ControlPanelView from "../components/admin/ControlPanelView";
import CompanyDetailsModal from "../components/admin/CompanyDetailsModal";
import ProductManagementModal from "../components/admin/ProductManagementModal";
import CreateSalesUserModal from "../components/admin/CreateSalesUserModal";
import SalesUsersModal from "../components/admin/SalesUsersModal";
import ProductSelectionModal from "../components/customer/ProductSelectionModal";

import SalesLayout from "../components/sales/SalesLayout";
import ClientsPage from "../components/sales/pages/clients/ClientsPage";
import ClientInteractionsPage from "../components/sales/pages/client-interactions/ClientInteractionsPage";
import SalesNotesPage from "../components/sales/pages/salesNotes/SalesNotesPage";
import SalesTemplatesPage from "../components/sales/pages/sales-templates/SalesTemplatesPage";
import ScheduledMessagesPage from "../components/sales/pages/scheduled-messages/ScheduledMessagesPage";
import MarketingDashboardPage from "../components/sales/pages/marketing-dashboard/MarketingDashboardPage";

import { useAuth } from "../context/AuthContext";
import { useAdminData } from "../hooks/useAdminData";
import { authService } from "../services/auth.service";
import { adminService } from "../services/admin.service";
import { productsService } from "../services/products.service";

// ─── Guards ───────────────────────────────────────────────────────────────────

function RequireRole({ authData, allowed, children }) {
  if (!authData) return <Navigate to="/login" replace />;
  if (!allowed.includes(authData.role)) return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect({ authData }) {
  if (!authData) return <Navigate to="/login" replace />;
  if (authData.role === "Admin") return <Navigate to="/control-panel" replace />;
  if (authData.role === "Sales") {
    return authData.needsPendingApproval
      ? <Navigate to="/pending" replace />
      : <Navigate to="/sales/clients" replace />;
  }
  if (authData.role === "Customer") {
    return authData.needsPendingApproval
      ? <Navigate to="/pending" replace />
      : <Navigate to="/products-dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AuthSystem() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Auth state from context (replaces local useState)
  const { authData, setAuthData, authInitialized, logout } = useAuth();

  // Admin data (companies + statistics) via hook
  const { companies, setCompanies, statistics, setStatistics, reload: reloadAdmin } =
    useAdminData(authData, logout);

  // Public products list (used in signup + customer dashboard)
  const [products, setProducts] = useState([]);

  // Signup form state
  const [signupForm, setSignupForm] = useState({
    managerName: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    password: "",
    companyNumber: "",
    companyAddress: "",
  });
  const [files, setFiles] = useState({
    commercialRegistration: null,
    taxNumber: null,
    nationalAddress: null,
    logo: null,
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductSelection, setShowProductSelection] = useState(false);

  // Login form state
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  // Shared UI state
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Admin modals state
  const [viewingCompany, setViewingCompany] = useState(null);
  const [showProductManagement, setShowProductManagement] = useState(false);
  const [showCreateSalesUser, setShowCreateSalesUser] = useState(false);
  const [showSalesUsersList, setShowSalesUsersList] = useState(false);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 4000);
  };

  const toIsoOrNull = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    return `${yyyyMmDd}T00:00:00.000Z`;
  };

  // Load public products on mount (used for signup + customer dashboard)
  useEffect(() => {
    (async () => {
      try {
        const data = await productsService.listPublic();
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        // silent — non-critical
      }
    })();
  }, []);

  // ─── Auth handlers ──────────────────────────────────────────────────────────

  const handleLogin = async () => {
    setLoading(true);
    try {
      const data = await authService.login(loginForm);
      setAuthData(data);
      navigate("/", { replace: true });
    } catch (e) {
      showMessage("error", e?.message || t("messages.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const buildRegisterFormData = () => {
    const fd = new FormData();
    fd.append("ManagerName", signupForm.managerName);
    fd.append("CompanyName", signupForm.companyName);
    fd.append("Email", signupForm.email);
    fd.append("PhoneNumber", signupForm.phoneNumber);
    fd.append("Password", signupForm.password);
    fd.append("CompanyNumber", signupForm.companyNumber);
    if (signupForm.companyAddress) fd.append("CompanyAddress", signupForm.companyAddress);
    selectedProducts.forEach((id) => fd.append("SelectedProductIds", id));
    Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
    return fd;
  };

  const handleSignup = async () => {
    if (!signupForm.managerName || !signupForm.email || !signupForm.password) {
      showMessage("error", t("validation.requiredSignupFields"));
      return;
    }
    setLoading(true);
    try {
      const fd = buildRegisterFormData();
      const data = await authService.register(fd);

      // Registration is NOT an auth session — clear any data the service stored
      // to prevent a role-less object from poisoning the auth context.
      const { storage } = await import("../services/storage");
      storage.clear();

      showMessage("success", t("messages.signupSuccess"));
      setTimeout(() => {
        navigate("/pending", {
          state: {
            companyName: signupForm.companyName,
            companyNumber: data?.companyNumber || data?.company?.companyNumber || "",
          },
        });
      }, 1500);
    } catch (e) {
      showMessage("error", e?.message || t("messages.networkError"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Admin handlers ─────────────────────────────────────────────────────────

  const handleUpdateCompany = async (companyId, editData) => {
    try {
      await adminService.updatePaymentStatus(companyId, !!editData?.hasPaid);

      const access = editData?.productAccess || {};
      const entries = Object.entries(access);
      if (entries.length > 0) {
        await Promise.all(
          entries.map(([productIdStr, v]) => {
            const hasAccess = !!v?.hasAccess;
            return productsService.assign({
              companyId: Number(companyId),
              productId: Number(productIdStr),
              hasPaid: !!v?.hasPaid,
              hasAccess,
              licenseStartDate: hasAccess ? toIsoOrNull(v?.startDate) : null,
              licenseEndDate: hasAccess ? toIsoOrNull(v?.endDate) : null,
            });
          })
        );
      }

      await reloadAdmin();
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        showMessage("error", t("messages.sessionExpired"));
        logout();
        return false;
      }
      alert(t("messages.updateCompanyErrorWithReason", { reason: e?.message || "Error" }));
      return false;
    }
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await adminService.deleteCompany(companyId);
      setViewingCompany(null);
      await reloadAdmin();
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        showMessage("error", t("messages.sessionExpired"));
        logout();
        return false;
      }
      showMessage("error", e?.message || t("messages.deleteCompanyError"));
      return false;
    }
  };

  const handleCreateSalesUser = async (dto) => {
    try {
      await adminService.createSalesUser(dto);
      await reloadAdmin();
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        showMessage("error", t("messages.sessionExpired"));
        logout();
        return false;
      }
      throw e; // let the modal's catch block show the real backend error
    }
  };

  const handleOpenProduct = (product) => {
    const url = product?.productUrl || product?.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  // Don't render routes until auth state is read from storage
  if (!authInitialized) return null;

  return (
    <div>
      <Routes>
        {/* Home — redirect based on role */}
        <Route path="/" element={<HomeRedirect authData={authData} />} />

        {/* ── Auth pages ── */}
        <Route
          path="/login"
          element={
            authData
              ? <HomeRedirect authData={authData} />
              : (
                <LoginView
                  loginForm={loginForm}
                  setLoginForm={setLoginForm}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  handleLogin={handleLogin}
                  loading={loading}
                  message={message}
                />
              )
          }
        />

        <Route
          path="/signup"
          element={
            <SignupView
              signupForm={signupForm}
              setSignupForm={setSignupForm}
              handleSignup={handleSignup}
              loading={loading}
              message={message}
              products={products}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              files={files}
              setFiles={setFiles}
              showProductSelection={showProductSelection}
              setShowProductSelection={setShowProductSelection}
            />
          }
        />

        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />

        {/* ── Pending ── */}
        <Route
          path="/pending"
          element={<PendingView authData={authData} onLogout={logout} />}
        />

        {/* ── Customer ── */}
        <Route
          path="/products-dashboard"
          element={
            <RequireRole authData={authData} allowed={["Customer"]}>
              <ProductsDashboard
                products={authData?.availableProducts || []}
                onSelectProduct={handleOpenProduct}
                onLogout={logout}
              />
            </RequireRole>
          }
        />

        {/* ── Admin ── */}
        <Route
          path="/control-panel"
          element={
            <RequireRole authData={authData} allowed={["Admin"]}>
              <ControlPanelView
                authData={authData}
                companies={companies}
                statistics={statistics}
                logout={logout}
                setViewingCompany={setViewingCompany}
                setShowProductManagement={setShowProductManagement}
                setShowCreateSalesUser={setShowCreateSalesUser}
                setShowSalesUsersList={setShowSalesUsersList}
              />
            </RequireRole>
          }
        />

        {/* ── Sales area (nested) ── */}
        <Route
          path="/sales"
          element={
            <RequireRole authData={authData} allowed={["Sales", "Admin"]}>
              <SalesLayout authData={authData} logout={logout} />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="clients" replace />} />
          <Route path="clients" element={<ClientsPage authData={authData} logout={logout} />} />
          <Route path="client-interactions" element={<ClientInteractionsPage authData={authData} logout={logout} />} />
          <Route path="sales-notes" element={<SalesNotesPage authData={authData} logout={logout} />} />
          <Route path="sales-templates" element={<SalesTemplatesPage authData={authData} logout={logout} />} />
          <Route path="scheduled-messages" element={<ScheduledMessagesPage authData={authData} logout={logout} />} />
          <Route path="marketing-dashboard" element={<MarketingDashboardPage authData={authData} logout={logout} />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* ── Global modals (rendered outside Routes) ── */}
      {viewingCompany && (
        <CompanyDetailsModal
          company={viewingCompany}
          onClose={() => setViewingCompany(null)}
          onUpdate={handleUpdateCompany}
          onDelete={handleDeleteCompany}
          isAdmin={authData?.role === "Admin"}
          allProducts={products}
          loading={loading}
        />
      )}

      {showProductManagement && (
        <ProductManagementModal
          onClose={() => setShowProductManagement(false)}
          onRefresh={async () => {
            try {
              const data = await productsService.listPublic();
              setProducts(Array.isArray(data) ? data : []);
            } catch {}
          }}
        />
      )}

      {showCreateSalesUser && (
        <CreateSalesUserModal
          onClose={() => setShowCreateSalesUser(false)}
          onSubmit={handleCreateSalesUser}
        />
      )}

      {showSalesUsersList && (
        <SalesUsersModal onClose={() => setShowSalesUsersList(false)} />
      )}

      {showProductSelection && (
        <ProductSelectionModal
          products={products}
          onContinue={(selected) => {
            setSelectedProducts(selected);
            setShowProductSelection(false);
          }}
          onClose={() => setShowProductSelection(false)}
        />
      )}
    </div>
  );
}
